import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Radio,
  RadioGroup,
  Textarea,
  useColorModeValue,
  useToast,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  Divider,
  Icon,
  Stack
} from '@chakra-ui/react';
import {
  MdGavel,
  MdCheckCircle,
  MdCancel,
  MdWarning,
  MdDescription
} from 'react-icons/md';
import { FaBalanceScale } from 'react-icons/fa';
import api from '../../../../../services/api';

const ParecerJuridicoModal = ({ isOpen, onClose, assignment, onSubmit }) => {
  const [recomendacao, setRecomendacao] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [observacoesLegais, setObservacoesLegais] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const handleSubmitParecer = async () => {
    if (!recomendacao || !justificativa) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha a recomendação e a justificativa',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSubmitting(true);
      
      await api.post(`/juridico/assignment/${assignment._id}/parecer`, {
        recomendacao,
        justificativa,
        observacoesLegais
      });
      
      toast({
        title: 'Parecer emitido',
        description: 'Parecer jurídico emitido com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Reset form
      setRecomendacao('');
      setJustificativa('');
      setObservacoesLegais('');
      
      onSubmit();
      onClose();
      
    } catch (error) {
      console.error('Error submitting parecer:', error);
      toast({
        title: 'Erro ao emitir parecer',
        description: error.response?.data?.message || 'Não foi possível emitir o parecer',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getRecomendacaoInfo = (value) => {
    const info = {
      prosseguir: {
        icon: MdCheckCircle,
        color: 'green',
        label: 'Prosseguir com o Processo',
        description: 'Todos os documentos estão em ordem e o processo pode continuar'
      },
      aguardar_correcoes: {
        icon: MdWarning,
        color: 'orange',
        label: 'Aguardar Correções',
        description: 'Existem documentos que precisam ser corrigidos antes de prosseguir'
      },
      recusar: {
        icon: MdCancel,
        color: 'red',
        label: 'Recusar Processo',
        description: 'Os documentos não atendem aos requisitos legais'
      },
      revisar: {
        icon: MdDescription,
        color: 'blue',
        label: 'Necessita Revisão',
        description: 'O processo precisa de uma análise mais detalhada'
      }
    };
    return info[value];
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FaBalanceScale} color="blue.500" />
            <Text>Emitir Parecer Jurídico</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Precatorio Info */}
            <Box p={3} bg="gray.50" borderRadius="md">
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold">
                  Precatório: {assignment?.numero_precatorio}
                </Text>
                <Badge colorScheme="purple">{assignment?.estado}</Badge>
              </HStack>
              <Text fontSize="sm" color={mutedColor}>
                Beneficiário: {assignment?.nome_beneficiario}
              </Text>
              <Text fontSize="sm" color={mutedColor}>
                CPF/CNPJ: {assignment?.cpf_cnpj}
              </Text>
              <Text fontSize="sm" color={mutedColor}>
                Valor: {formatCurrency(assignment?.valor_atualizado)}
              </Text>
            </Box>

            <Divider />

            {/* Analysis Summary */}
            <Alert status="info">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Resumo da Análise</Text>
                <Text fontSize="sm">
                  Documentos Aprovados: {assignment?.analysisProgress?.approvedDocuments || 0} | 
                  Reprovados: {assignment?.analysisProgress?.reprovedDocuments || 0}
                </Text>
              </Box>
            </Alert>

            {/* Recomendação */}
            <FormControl isRequired>
              <FormLabel>Recomendação</FormLabel>
              <RadioGroup value={recomendacao} onChange={setRecomendacao}>
                <Stack spacing={3}>
                  {['prosseguir', 'aguardar_correcoes', 'recusar', 'revisar'].map(option => {
                    const info = getRecomendacaoInfo(option);
                    return (
                      <Box
                        key={option}
                        p={3}
                        borderWidth={1}
                        borderColor={recomendacao === option ? `${info.color}.500` : borderColor}
                        borderRadius="md"
                        bg={recomendacao === option ? `${info.color}.50` : 'transparent'}
                        cursor="pointer"
                        onClick={() => setRecomendacao(option)}
                      >
                        <Radio value={option} colorScheme={info.color}>
                          <HStack spacing={2}>
                            <Icon as={info.icon} color={`${info.color}.500`} />
                            <Box>
                              <Text fontWeight="semibold">{info.label}</Text>
                              <Text fontSize="xs" color={mutedColor}>
                                {info.description}
                              </Text>
                            </Box>
                          </HStack>
                        </Radio>
                      </Box>
                    );
                  })}
                </Stack>
              </RadioGroup>
              <FormHelperText>
                Selecione a recomendação baseada na análise dos documentos
              </FormHelperText>
            </FormControl>

            {/* Justificativa */}
            <FormControl isRequired>
              <FormLabel>Justificativa</FormLabel>
              <Textarea
                placeholder="Descreva detalhadamente os motivos da sua recomendação..."
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={4}
              />
              <FormHelperText>
                Explique os fundamentos legais e técnicos da sua decisão
              </FormHelperText>
            </FormControl>

            {/* Observações Legais */}
            <FormControl>
              <FormLabel>Observações Legais (Opcional)</FormLabel>
              <Textarea
                placeholder="Adicione observações legais relevantes, ressalvas ou recomendações adicionais..."
                value={observacoesLegais}
                onChange={(e) => setObservacoesLegais(e.target.value)}
                rows={3}
              />
              <FormHelperText>
                Informações adicionais que possam ser relevantes para o processo
              </FormHelperText>
            </FormControl>

            {/* Warning */}
            {recomendacao === 'recusar' && (
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">
                  Ao recusar o processo, o precatório retornará ao status "Proposta Negada".
                  Certifique-se de que a justificativa está clara e completa.
                </Text>
              </Alert>
            )}

            {recomendacao === 'prosseguir' && (
              <Alert status="success">
                <AlertIcon />
                <Text fontSize="sm">
                  Ao aprovar o processo, o precatório será marcado como "Fechado" e 
                  poderá prosseguir para as próximas etapas.
                </Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<MdGavel />}
            onClick={handleSubmitParecer}
            isLoading={submitting}
            isDisabled={!recomendacao || !justificativa}
          >
            Emitir Parecer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ParecerJuridicoModal;